import { createApp } from "vue";
import * as Sentry from "@sentry/vue";
import { vfmPlugin } from "vue-final-modal";
import App from "./App.vue";
import axiosPlugin from "./axiosPlugin.mjs";
import router, { authIsCached } from "./router";
import {
  sanitizeSentryEvent,
  prepareGlitchtipEventIdForFeedback,
  sentryTunnelOption,
} from "./utilities/sentry";
import { installSentryDiagnostics } from "./utilities/sentryDiagnostics";
import {
  initUmami,
  identifyUmamiUser,
  trackUmamiEvent,
  getUmamiWebsiteId,
} from "./utilities/umami";
import { getCorrelationId } from "./utilities/correlationId";
import store from "./store";
import translates from "./utilities/translates";
import { createI18n } from "vue-i18n";
import ToastPlugin from "vue-toast-notification";
import "vue-toast-notification/dist/theme-bootstrap.css";
// Modulo Assistenza/Feedback (v1.1.0): installer + adapter (axios/router iniettati)
import axiosInstance from "@/axiosInstance";
import { installFeedbackModule } from "@/modules/feedback";
import "@/css/tailwind.css";
import AppSelect from "@/components/AppSelect.vue";

const app = createApp(App);
// Create a new instance of VueI18n
const i18n = createI18n({
  locale: "it",
  fallbackLocale: "en",
  messages: translates,
});
app.use(axiosPlugin);
app.use(store);
app.use(ToastPlugin, {
  position: "top-right", // forza il posizionamento
  duration: 3500, // ms
  dismissible: true, // mostra la X
  pauseOnHover: true,
  queue: false,
});
app.use(i18n);
app.use(
  vfmPlugin({
    key: "$vfm",
    componentName: "VueFinalModal",
    dynamicContainerName: "ModalsContainer",
  })
);
// [sc-49210] Menu a tendina unico dell'app, al posto dei <select> nativi
app.component("AppSelect", AppSelect);
app.use(router);

// Id di correlazione cross-provider (ticket ↔ GlitchTip ↔ Umami) [sc-47909].
// Anonimo e random: NON deriva da utente/email. Vedi utilities/correlationId.js.
const correlationId = getCorrelationId();

// GlitchTip/Sentry: init SOLO se il DSN è presente (staging/prod). In dev senza DSN nessun rumore.
// UN SOLO progetto GlitchTip EmmaMaison condiviso web+api; distinzione via appId [sc-47544].
// MVP error-tracking: performance/tracing default 0. beforeSend redige PII/segreti/query.
const sentryDsn = process.env.VUE_APP_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    app,
    dsn: sentryDsn,
    // Tunnel first-party [sc-47917 F2B]: se VUE_APP_SENTRY_TUNNEL_URL è valorizzata,
    // gli envelope passano dall'endpoint API first-party emmamaison-api e non
    // dall'ingest diretto. Assente la var → nessun tunnel, invio diretto come prima.
    // La DSN resta invariata.
    ...sentryTunnelOption(process.env.VUE_APP_SENTRY_TUNNEL_URL),
    environment: process.env.VUE_APP_NODE_ENV || process.env.NODE_ENV,
    release: process.env.VUE_APP_SENTRY_RELEASE || undefined,
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: Number(
      process.env.VUE_APP_SENTRY_TRACES_SAMPLE_RATE || 0
    ),
    autoSessionTracking: false,
    beforeSend: sanitizeSentryEvent,
  });
  // Tag globale appId: distingue gli eventi di emmamaison-web da emmamaison-api nello stesso progetto.
  Sentry.setTag("appId", process.env.VUE_APP_APP_ID || "emmamaison-web");
  // Correlazione ticket assistenza ↔ evento GlitchTip. Tag (valore scalare) e non
  // setContext: sanitizeSentryEvent redige i contexts solo al primo livello.
  Sentry.setTag("lt_correlation_id", correlationId);
  // Correla ogni evento alla route corrente (senza query string).
  // NB: nessun tag userRole — gli Operator EmmaMaison non hanno un ruolo affidabile e non
  // vogliamo PII; si potrà aggiungere un userHash quando ci sarà un id operatore stabile.
  router.afterEach((to) => {
    Sentry.setTag("routePath", to.path);
  });
  // Diagnostica staging [sc-47917 F2B]: installa window.__LT_SENTRY_DIAGNOSTICS__
  // SOLO se VUE_APP_SENTRY_DIAGNOSTICS === "true". No-op altrimenti. Nessun auto-trigger.
  installSentryDiagnostics();
}

// Modulo Assistenza/Feedback: registra SOLO il widget user-facing globale (bolla + modale).
// NESSUNA route pannello nel CRM: non passiamo `router`, quindi il pannello interno/admin
// non viene registrato. Nel CRM tutti sono richiedenti assistenza; il support/admin vive su
// assistenza-central [sc-47544 8C-Emma-6]. Il widget non chiama API se l'utente non è autenticato.
installFeedbackModule({
  app,
  axiosInstance,
  isAuthenticated: () => authIsCached(),
  notify: (message, options) =>
    app.config.globalProperties.$toast?.open?.({
      message,
      type: options?.type || "info",
    }),
  // Osservabilità [sc-47909]: il modulo non importa nulla dal CRM, riceve tutto
  // qui. I campi tecnici viaggiano col ticket verso central (top-level + context).
  // ASYNC [sc-47917]: prima di allegare glitchtipEventId tentiamo un flush breve
  // della coda Sentry; se non si svuota, l'id NON viene inviato (meglio vuoto che
  // un id mai consegnato a GlitchTip). Gli altri campi restano sempre presenti.
  getObservability: async () => ({
    lt_correlation_id: correlationId,
    appId: process.env.VUE_APP_APP_ID || "emmamaison-web",
    release: process.env.VUE_APP_SENTRY_RELEASE || "",
    glitchtipEventId: await prepareGlitchtipEventIdForFeedback({
      timeoutMs: 2000,
    }),
    umamiWebsiteId: getUmamiWebsiteId(),
  }),
  track: (name, data) =>
    trackUmamiEvent(name, {
      ...(data || {}),
      lt_correlation_id: correlationId,
    }),
});

app.mount("#app");

// Umami analytics (self-hosted): iniezione dello script del tracker una sola
// volta, indipendente e non bloccante rispetto a Sentry. No-op se le env
// VUE_APP_UMAMI_* non sono configurate. Auto-track SPA attivo (nessun tracking
// manuale via router per evitare pageview duplicate).
initUmami();
// Associa la sessione Umami allo stesso id anonimo usato da ticket e GlitchTip.
// Best-effort: lo script è `defer`, identifyUmamiUser ritenta per poco e poi molla.
identifyUmamiUser(correlationId, { lt_correlation_id: correlationId });
// Rendi axios disponibile globalmente
// app.config.globalProperties.$axios = axios;
